import { ref, Ref } from 'vue';
import { useNotify, NotifyType } from '@/composables/useNotify';
import { useI18n } from 'vue-i18n';
import { hubClient, getApiErrorMessage } from '@tfpl/api-client';
import { getConfig } from '@/config';

interface UseDocument {
  loading: Ref<boolean>;
  error: Ref<Error>;
  showError: Ref<boolean>;
  deleteDocument: (uuid: string) => Promise<void>;
  createDocument: (dto: hubClient.CreateDocumentDto) => Promise<void>;
  updateDocument: (uuid: string, dto: hubClient.UpdateDocumentDto) => Promise<void>;
}

export const useDocument: () => UseDocument = () => {
  const { notify } = useNotify();
  const { t } = useI18n();
  const loading = ref(false);
  const error = ref();
  const showError = ref(false);
  const setError = (err: boolean): void => {
    showError.value = err;
  };

  const createDocument = async (dto: hubClient.CreateDocumentDto) => {
    try {
      loading.value = true;
      await hubClient.V3DocumentsService.createOneV3({
        path: {
          tenant: getConfig().TENANT,
          company: getConfig().TENANT,
        },
        body: dto,
        throwOnError: true,
      });
      notify(t('document.created'), NotifyType.SUCCESS);
    } catch (e) {
      error.value = e;
      setError(true);
      notify(t('common.requestError'), NotifyType.ERROR);
      console.log('[error]', getApiErrorMessage(e) ?? e);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const updateDocument = async (uuid: string, dto: hubClient.UpdateDocumentDto) => {
    try {
      loading.value = true;
      await hubClient.V3DocumentsService.updateOneV3({
        path: {
          tenant: getConfig().TENANT,
          company: getConfig().TENANT,
          uuid,
        },
        body: dto,
        throwOnError: true,
      });
    } catch (e) {
      error.value = e;
      setError(true);
      notify(t('common.requestError'), NotifyType.ERROR);
      console.log('[error]', getApiErrorMessage(e) ?? e);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const deleteDocument = async (uuid: string) => {
    try {
      loading.value = true;
      await hubClient.V3DocumentsService.deleteOneV3({
        path: {
          tenant: getConfig().TENANT,
          company: getConfig().TENANT,
          uuid,
        },
        throwOnError: true,
      });
    } catch (e) {
      error.value = e;
      setError(true);
      notify(t('common.requestError'), NotifyType.ERROR);
      console.log('[error]', getApiErrorMessage(e) ?? e);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  return {
    loading,
    error,
    showError,
    createDocument,
    updateDocument,
    deleteDocument,
  };
};
